import React, { useMemo } from 'react';
import { Contact, ContactFilters } from '../../../config/api';
import ContactCard from './ContactCard';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

interface ContactsKanbanViewProps {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  filters: ContactFilters;
  onContactClick: (contact: Contact) => void;
  onStatusChange?: (contactId: string, newStatus: string) => void;
}

const statusOrder = ['lead', 'qualified', 'converted', 'archived'] as const;

type StatusType = typeof statusOrder[number];

const ContactsKanbanView: React.FC<ContactsKanbanViewProps> = ({
  contacts,
  loading,
  error,
  searchQuery,
  filters,
  onContactClick,
  onStatusChange
}) => {
  // Filter contacts
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          contact.name.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.phone?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && contact.status !== filters.status) {
        return false;
      }

      // Type filter
      if (filters.contact_type && contact.contact_type !== filters.contact_type) {
        return false;
      }

      return true;
    });
  }, [contacts, searchQuery, filters]);

  // Group contacts by status
  const contactsByStatus = useMemo(() => {
    const grouped: Record<StatusType, Contact[]> = {
      lead: [],
      qualified: [],
      converted: [],
      archived: []
    };
    filteredContacts.forEach(contact => {
      if (grouped[contact.status as StatusType]) {
        grouped[contact.status as StatusType].push(contact);
      }
    });
    return grouped;
  }, [filteredContacts]);

  const getStatusConfig = (status: StatusType) => {
    switch (status) {
      case 'lead':
        return {
          title: 'Leads',
          color: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-700',
          textColor: 'text-yellow-800 dark:text-yellow-200'
        };
      case 'qualified':
        return {
          title: 'Qualified',
          color: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-700',
          textColor: 'text-blue-800 dark:text-blue-200'
        };
      case 'converted':
        return {
          title: 'Converted',
          color: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-700',
          textColor: 'text-green-800 dark:text-green-200'
        };
      case 'archived':
        return {
          title: 'Archived',
          color: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-700',
          textColor: 'text-gray-800 dark:text-gray-200'
        };
      default:
        return {
          title: status,
          color: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-700',
          textColor: 'text-gray-800 dark:text-gray-200'
        };
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    const sourceStatus = source.droppableId as StatusType;
    const destStatus = destination.droppableId as StatusType;
    if (sourceStatus !== destStatus && onStatusChange) {
      onStatusChange(draggableId, destStatus);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading contacts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statusOrder.map((status) => {
          const config = getStatusConfig(status);
          const statusContacts = contactsByStatus[status];
          return (
            <Droppable droppableId={status} key={status}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`${config.color} ${config.borderColor} border rounded-lg p-4 min-h-[600px] transition-shadow ${snapshot.isDraggingOver ? 'ring-2 ring-lex-accent' : ''}`}
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-semibold ${config.textColor}`}>{config.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color} ${config.textColor}`}>
                      {statusContacts.length}
                    </span>
                  </div>
                  {/* Contacts in this column */}
                  <div className="space-y-3">
                    {statusContacts.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 dark:text-gray-500">
                          <svg className="mx-auto h-8 w-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className="text-sm">No contacts</p>
                        </div>
                      </div>
                    ) : (
                      statusContacts.map((contact, idx) => (
                        <Draggable draggableId={contact.id} index={idx} key={contact.id}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`w-full ${snapshot.isDragging ? 'ring-2 ring-lex-accent' : ''}`}
                            >
                              <ContactCard
                                contact={contact}
                                onClick={() => onContactClick(contact)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default ContactsKanbanView; 